import React, { useState, useRef } from 'react';
import { Card, Spin, message, Modal, Input, Button, Dropdown } from 'antd';
import { UserOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApartmentUnitsByBlock, updateApartmentUnit } from '@/api/properties';
import { addResident } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import { ResidentListTab } from './ResidentListTab';
import ResidentFormModal from '@/components/features/resources/residents/ResidentFormModal';
import { Resident } from '@/types/resources/resident';
import type { CreateResidentInput } from '@/api/residents';

interface FloorsTabProps {
  blockId: string;
  blockName: string;
  floors: Array<{
    name: string;
    slug: string;
    apartmentUnits?: Array<{
      number: string;
      name: string;
      slug: string;
    }>;
  }>;
  onResidentAssigned?: () => void;
}

const FloorsTab: React.FC<FloorsTabProps> = ({ 
  blockId, 
  blockName, 
  floors, 
  onResidentAssigned 
}) => {
  const { t } = useTranslation(['properties', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [showResidentSelector, setShowResidentSelector] = useState(false);
  const [showResidentForm, setShowResidentForm] = useState(false);
  const [selectedApartmentUnit, setSelectedApartmentUnit] = useState<{ 
    id: string; 
    number: string; 
    floor: string 
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch apartment units for this block
  const { data: apartmentUnitsData, isLoading: apartmentUnitsLoading, error: apartmentUnitsError } = useQuery({
    queryKey: ['apartmentUnitsByBlock', blockId, firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser || !blockId) throw new Error('User not authenticated or no block ID');
      return getApartmentUnitsByBlock(blockId, firestoreUser);
    },
    enabled: !!blockId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  // Mutation for updating apartment unit with resident
  const updateApartmentUnitMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateApartmentUnit(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success('Resident assigned successfully');
      queryClient.invalidateQueries({ queryKey: ['apartmentUnitsByBlock', blockId] });
      setShowResidentSelector(false);
      setSelectedApartmentUnit(null);
      setSearchTerm('');
      onResidentAssigned?.();
    },
    onError: (error: Error) => {
      message.error('Failed to assign resident');
      console.error('Error updating apartment unit:', error);
    }
  });

  // Handle resident selection for apartment unit
  const handleResidentSelect = (resident: Resident) => {
    if (selectedApartmentUnit) {
      updateApartmentUnitMutation.mutate({
        id: selectedApartmentUnit.id,
        data: {
          resident: {
            id: resident.id,
            name: resident.user?.displayName || resident.contacts?.[0]?.name || 'Unknown'
          },
          status: 'occupied'
        }
      });
    }
  };

  // Handle adding new resident
  const handleAddNewResident = (apartmentUnitId: string, unitNumber: string, floorName: string) => {
    setSelectedApartmentUnit({
      id: apartmentUnitId,
      number: unitNumber,
      floor: floorName
    });
    setShowResidentForm(true);
  };

  // Handle searching for existing resident
  const handleSearchResident = (apartmentUnitId: string, unitNumber: string, floorName: string) => {
    setSelectedApartmentUnit({
      id: apartmentUnitId,
      number: unitNumber,
      floor: floorName
    });
    setShowResidentSelector(true);
  };

  // Handle resident form submission
  const handleResidentFormSubmit = async (values: CreateResidentInput) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      console.log('Creating resident with values:', values);
      const residentId = await addResident(values, firestoreUser);
      console.log('Resident created successfully with ID:', residentId);
      
      message.success('Resident added successfully');
      setShowResidentForm(false);
      setSelectedApartmentUnit(null);
      queryClient.invalidateQueries({ queryKey: ['apartmentUnitsByBlock', blockId] });
      onResidentAssigned?.();
    } catch (error) {
      console.error('Error creating resident:', error);
      message.error(error instanceof Error ? error.message : 'Failed to create resident');
    }
  };

  // Handle removing resident from apartment unit
  const handleRemoveResident = (apartmentUnitId: string) => {
    const apartmentUnit = apartmentUnitsData?.apartmentUnits.find(unit => unit.id === apartmentUnitId);
    const residentName = apartmentUnit?.resident?.name || 'Unknown';
    
    Modal.confirm({
      title: 'Remove Resident',
      content: `Are you sure you want to remove ${residentName} from this apartment unit?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        updateApartmentUnitMutation.mutate({
          id: apartmentUnitId,
          data: {
            resident: undefined,
            status: 'vacant'
          }
        });
      }
    });
  };

  // Get apartment unit data for a specific floor and unit number
  const getApartmentUnitData = (floorName: string, unitNumber: string) => {
    if (!apartmentUnitsData?.apartmentUnits) return null;
    
    return apartmentUnitsData.apartmentUnits.find(unit => 
      unit.floor?.name === floorName && unit.number === unitNumber
    );
  };

  if (apartmentUnitsLoading) {
    return (
      <Card>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '40px',
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <Spin size="large" />
          <span style={{ marginLeft: 16, color: '#666' }}>Loading apartment units...</span>
        </div>
      </Card>
    );
  }

  if (apartmentUnitsError) {
    return (
      <Card>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '40px',
          backgroundColor: '#fff2f0',
          borderRadius: '8px',
          border: '1px solid #ffccc7',
          color: '#cf1322'
        }}>
          <span>Failed to load apartment units. Please try refreshing the page.</span>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        {/* Block Name Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '18px', 
            color: '#1890ff',
            fontWeight: 'bold'
          }}>
            {blockName}
          </h3>
        </div>

        {/* Apartment Building Visualization */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column-reverse', // Show ground floor at bottom
          gap: '2px',
          border: '2px solid #d9d9d9',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#fafafa',
          maxHeight: '600px',
          overflowY: 'auto'
        }}>
          {floors?.slice().reverse().map((floor, floorIndex) => (
            <div key={floorIndex} style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '8px',
              padding: '12px',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px solid #e8e8e8'
            }}>
              {/* Floor Info and Actions Row */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #f0f0f0',
                paddingBottom: '8px'
              }}>
                {/* Floor Label */}
                <div style={{ 
                  fontWeight: 'bold',
                  color: '#1890ff',
                  fontSize: '14px'
                }}>
                  {t('units.fields.floor')}: {floor.name}
                </div>
                
                {/* Floor Actions */}
                <Button 
                  type="text" 
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => {
                    // Navigate to floor details if needed
                    console.log('Edit floor:', floor.slug);
                  }}
                >
                  {t('common:actions.edit')}
                </Button>
              </div>

              {/* Apartment Units Row */}
              <div style={{ 
                display: 'flex', 
                gap: '4px',
                flexWrap: 'wrap',
                justifyContent: 'flex-start'
              }}>
                {floor.apartmentUnits?.map((unit, unitIndex) => {
                  const apartmentUnitData = getApartmentUnitData(floor.name, unit.number);
                  const isOccupied = apartmentUnitData?.resident;
                  
                  return (
                    <Dropdown
                      key={unitIndex}
                      menu={{
                        items: isOccupied ? [
                          {
                            key: 'remove',
                            label: t('floors.tooltip.clickToRemove'),
                            icon: <EditOutlined />,
                            onClick: () => {
                              if (apartmentUnitData?.id) {
                                handleRemoveResident(apartmentUnitData.id);
                              }
                            }
                          }
                        ] : [
                          {
                            key: 'add-new',
                            label: t('floors.actions.addNewResident'),
                            icon: <PlusOutlined />,
                            onClick: () => {
                              if (apartmentUnitData?.id) {
                                handleAddNewResident(apartmentUnitData.id, unit.number, floor.name);
                              }
                            }
                          },
                          {
                            key: 'search',
                            label: t('floors.actions.searchResident'),
                            icon: <SearchOutlined />,
                            onClick: () => {
                              if (apartmentUnitData?.id) {
                                handleSearchResident(apartmentUnitData.id, unit.number, floor.name);
                              }
                            }
                          }
                        ]
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      destroyPopupOnHide={true}
                    >
                        <div
                          ref={dropdownRef}
                          style={{
                            width: '50px',
                            height: '40px',
                            backgroundColor: isOccupied ? '#52c41a' : '#f0f0f0',
                            border: `2px solid ${isOccupied ? '#389e0d' : '#d9d9d9'}`,
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: '500',
                            color: isOccupied ? '#fff' : '#595959',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            position: 'relative',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                            e.currentTarget.style.borderColor = isOccupied ? '#ff4d4f' : '#1890ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = isOccupied ? '#389e0d' : '#d9d9d9';
                          }}
                        >
                        <div style={{ fontSize: '8px', lineHeight: '1' }}>
                          {unit.number}
                        </div>
                        {isOccupied && (
                          <div style={{ 
                            position: 'absolute', 
                            top: '-2px', 
                            right: '-2px',
                            width: '12px',
                            height: '12px',
                            backgroundColor: '#fff',
                            border: '1px solid #52c41a',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <UserOutlined style={{ fontSize: '8px', color: '#52c41a' }} />
                          </div>
                        )}
                        </div>
                    </Dropdown>
                  );
                }) || (
                  // Fallback if no apartment units
                  <div style={{
                    width: '50px',
                    height: '40px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#999',
                    flexShrink: 0
                  }}>
                    -
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{ 
          marginTop: '16px', 
          padding: '16px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '6px', 
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>{t('floors.legend.title')}</h4>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '16px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #d9d9d9',
                borderRadius: '4px'
              }} />
              <span style={{ fontSize: '12px' }}>{t('floors.legend.vacantUnit')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '16px',
                backgroundColor: '#52c41a',
                border: '1px solid #389e0d',
                borderRadius: '4px',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-1px',
                  right: '-1px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#fff',
                  border: '1px solid #52c41a',
                  borderRadius: '50%'
                }} />
              </div>
              <span style={{ fontSize: '12px' }}>{t('floors.legend.occupiedUnit')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
              <span style={{ fontSize: '12px' }}>{t('floors.legend.clickToAssign')}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Resident Selector Modal */}
      <Modal
        title={t('floors.modal.assignResidentTitle', { 
          unitNumber: selectedApartmentUnit?.number, 
          floorName: selectedApartmentUnit?.floor 
        })}
        open={showResidentSelector}
        onCancel={() => {
          setShowResidentSelector(false);
          setSelectedApartmentUnit(null);
          setSearchTerm('');
        }}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, color: '#666' }}>
            Select a resident to assign to this apartment unit. Only unassigned residents are shown.
          </p>
          <Input
            placeholder="Search residents..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            style={{ marginTop: 12 }}
            prefix={<UserOutlined />}
            allowClear
          />
        </div>
        <ResidentListTab
          onSelect={handleResidentSelect}
          searchTerm={searchTerm}
          excludeAssigned={true}
        />
      </Modal>

      {/* Resident Form Modal — key remounts per unit so defaults and initial values apply */}
      <ResidentFormModal
        key={showResidentForm && selectedApartmentUnit ? selectedApartmentUnit.id : 'resident-form-closed'}
        visible={showResidentForm}
        onCancel={() => {
          setShowResidentForm(false);
          setSelectedApartmentUnit(null);
        }}
        onSubmit={handleResidentFormSubmit}
        title={t('floors.modal.addResidentTitle', { 
          unitNumber: selectedApartmentUnit?.number, 
          floorName: selectedApartmentUnit?.floor 
        })}
        initialValues={{
          type: 'owner',
          status: 'pending',
          moveInDate: new Date(),
          apartmentUnit: selectedApartmentUnit ? {
            id: selectedApartmentUnit.id,
            number: selectedApartmentUnit.number
          } : undefined,
          block: {
            id: blockId,
            name: blockName
          }
        }}
      />
    </>
  );
};

export default FloorsTab;
